import React, { useState, useMemo, useCallback } from "react";
import CreatableSelect from "react-select/lib/Creatable";
import { InfoContainer, InfoText } from "../ConfirmModal/confirmModalComponents";
import PencilImg from "../../assets/img/pencil-edit-button.svg";

export default function InfoContainerComponent({
  title,
  value,
  canEdit,
  options,
  handleChange,
  setOptions,
  backgroundColor = "transparent",
  maskValue,
  cleanValue,
  validate,
  normalizeOnSave,
  onError,
}) {
  const [editInfo, setEditInfo] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState(null);

  function identityFn(v) {
    return v;
  }

  function defaultValidateFn() {
    return { ok: true };
  }

  const mask = typeof maskValue === "function" ? maskValue : identityFn;
  const cleaner = typeof cleanValue === "function" ? cleanValue : identityFn;
  const doValidate = typeof validate === "function" ? validate : defaultValidateFn;

  const displayOptions = useMemo(() => (options || []).map((o) => {
    const base = o.label ? o.label : (o.value ? o.value : "");
    return {
      ...o,
      label: mask(base),
      value: o.value ? o.value : base,
    };
  }), [options, mask]);

  const selectedDisplayOption = useMemo(() => {
    const currentClean = cleaner(value || "");
    let found = null;

    (options || []).forEach((o) => {
      const optValue = o.value ? o.value : (o.label ? o.label : "");
      if (optValue === currentClean && !found) {
        found = o;
      }
    });

    if (found) {
      const lbl = found.label ? found.label : (found.value ? found.value : "");
      return { ...found, label: mask(lbl) };
    } if (currentClean) {
      return { value: currentClean, label: mask(currentClean) };
    }
    return null;
  }, [options, value, cleaner, mask]);

  const setErrorMessage = useCallback((msg) => {
    setError(msg || null);
    if (typeof onError === "function") {
      onError(msg || null);
    }
  }, [onError]);

  const commitValue = useCallback((raw) => {
    const toSaveRaw = normalizeOnSave ? cleaner(raw, { phase: "commit" }) : raw;
    const verdict = doValidate(toSaveRaw);

    if (!verdict.ok) {
      setErrorMessage(verdict.message || "Valor inválido.");
      return false;
    }

    setErrorMessage(null);
    handleChange(toSaveRaw);
    return true;
  }, [normalizeOnSave, cleaner, doValidate, handleChange, setErrorMessage]);

  const handleSelect = useCallback((selectedOption) => {
    if (selectedOption) {
      const selectedVal = selectedOption.value
        ? selectedOption.value
        : (selectedOption.label ? selectedOption.label : "");

      if (commitValue(selectedVal)) {
        setEditInfo(false);
      }
    } else {
      handleChange("");
      setErrorMessage(null);
      setEditInfo(false);
    }
  }, [commitValue, handleChange]);

  const handleCreate = useCallback((newValue) => {
    if (!newValue) return;

    const raw = normalizeOnSave ? cleaner(newValue, { phase: "commit" }) : newValue;
    if (!commitValue(raw)) return;

    const newOption = { value: raw, label: raw };

    setOptions((prevOptions) => {
      const arr = prevOptions || [];
      const exists = arr.some((o) => {
        const base = o.value ? o.value : (o.label ? o.label : "");
        return base === raw;
      });

      return exists ? arr : arr.concat([newOption]);
    });

    setEditInfo(false);
  }, [normalizeOnSave, cleaner, commitValue, setOptions]);

  const handleBlur = useCallback(() => {
    const current = inputValue && inputValue.trim ? inputValue.trim() : "";
    if (current) {
      handleCreate(current);
    } else {
      setEditInfo(false);
    }
  }, [inputValue, handleCreate]);

  const handleInputChange = useCallback((val) => {
    const next = cleaner(val || "", { phase: "typing" });
    const verdict = doValidate(next);
    setErrorMessage(verdict.ok ? null : (verdict.message || "Valor inválido."));
    setInputValue(next);
    return next;
  }, [cleaner, doValidate]);


  const formatCreateLabel = useCallback(
    (newValue) => `Adicionar "${mask(newValue)}"`,
    [mask]
  );
  return (
    <InfoContainer>
      <h3 style={{ fontWeight: "400" }}>{title}</h3>
      <div style={{
        margin: "auto", marginTop: "10px", maxWidth: "95%", backgroundColor
      }}
      >
        {canEdit ? (
          <div>
            {!editInfo ? (
              <div
                onClick={function handleEditClick() {
                  setEditInfo(true);
                }}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <InfoText>{mask(value) || "Não Informado"}</InfoText>
                <img src={PencilImg} alt="edit" style={{ height: "0.8rem" }} />
              </div>
            ) : (
              <div>
                <CreatableSelect
                  style={{ marginTop: "10px" }}
                  isClearable
                  options={displayOptions}
                  onChange={handleSelect}
                  onCreateOption={handleCreate}
                  value={selectedDisplayOption}
                  placeholder="Não Informado"
                  onInputChange={handleInputChange}
                  onBlur={handleBlur}
                  autoFocus
                  menuIsOpen
                  formatCreateLabel={formatCreateLabel}
                />
                {error ? (
                  <div
                    role="alert"
                    style={{ color: "#b00020", fontSize: 12, marginTop: 6 }}
                  >
                    {error}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : (
          <InfoText>{mask(value) || "Não Informado"}</InfoText>
        )}
      </div>
    </InfoContainer>
  );
}
